/**
 * Reusable result cards rendered inline in the assistant chat bubble.
 *
 * When a tool matches structured results (e.g. specific emails), the agent
 * emits per-result `result.card` uiActions. useChat partitions those out and
 * appends `RESULT_CARDS_MARKER + JSON(ResultCard[])` to the FINAL assistant
 * message content (so the cards survive persistence + reload). ChatBox detects
 * the marker via {@link splitResultCards} and renders <ResultCardRenderer>
 * after the plain markdown body.
 */

export const RESULT_CARDS_MARKER = '@@XAVIOR_RESULT_CARDS@@';

// 'today' is the pull "what's on my plate" card; 'briefing' is its legacy kind
// (older persisted messages carry it) — both render the same card.
// 'artifact' is the Studio v2 inline draft surface (foundation-spec §6.3, D18) —
// a live ArtifactCard generalizing the static 'report' CTA. Its `data` is the
// studio `ArtifactCardData` (the full structured ArtifactContent + resolver
// inputs); ResultCardRenderer casts to it at the call site (the type lives in
// `@/modules/pages/studio/draft`, which `features/` cannot import here).
// 'contact_group' is the grouped-lookup card (group contacts by company/etc.).
export type ResultCardKind = 'email' | 'note' | 'doc' | 'contact' | 'contact_group' | 'place' | 'task' | 'email_draft' | 'events' | 'inbox' | 'inbox_triage' | 'today' | 'briefing' | 'report' | 'artifact' | 'travel' | 'reservations' | 'circle' | 'persona' | 'social' | 'bulk_delete_confirm' | 'board_archive_confirm';

/** One curated row on the inbox-triage card. */
export interface InboxTriageRow {
  kind: 'person' | 'delivery' | 'security' | 'finance' | 'event' | 'notice';
  senderName: string;
  senderEmail: string;
  subject: string;
  snippet: string;
  date: string;
  isUnread: boolean;
  threadId: string;
  messageId: string;
  integrationId: string;
  senderPhotoUrl?: string | null;
}

/** Payload of the `inbox_triage` card — people needing replies + notable
 *  notifications, with the filtered noise counted, not shown. */
export interface InboxTriageCardData {
  people: InboxTriageRow[];
  notifications: InboxTriageRow[];
  hiddenCount: number;
  duplicates: number;
}

/** One event inside an {@link EventsCardData}. Times are ISO 8601. */
export interface EventsCardEvent {
  /** Unique row id (for recurring events this is `${masterId}:${occurrenceISO}`,
   *  so it stays unique per occurrence for React keys). */
  id: string;
  /** Real workspace OBJECT id used for click-through. Differs from `id` for a
   *  recurring occurrence (it's the master event's id); falls back to `id`. */
  objectId?: string;
  title: string;
  start: string;
  end?: string;
  allDay?: boolean;
  location?: string;
  attendees?: Array<{ name?: string; email: string; photoUrl?: string | null }>;
}

/** ONE card holding a schedule for a date range — a glanceable week strip plus a
 *  day-grouped agenda. The agent emits this for "what's on my schedule" queries. */
export interface EventsCardData {
  /** Human label for the range, e.g. "Next week" or "Jun 2 – 8". */
  rangeLabel: string;
  /** ISO 8601 bounds of the queried window (drive the week strip + Open Calendar). */
  rangeStart: string;
  rangeEnd: string;
  events: EventsCardEvent[];
  /**
   * LIVE-VIEW anchor (card↔calendar alignment). The unique workspace OBJECT ids
   * (recurring-master ids, occurrence suffix stripped) the agent surfaced in this
   * card. When PRESENT, {@link EventsResultCard} re-reads these objects from the
   * SAME TanStack objects cache the calendar grid uses (`useObjectsByType('event',
   * …)`) and renders LIVE — so a later delete/edit/recurrence change reconciles
   * the card exactly like the grid, instead of showing the frozen agent snapshot.
   *
   * ABSENT on legacy persisted cards (serialized before this field existed): those
   * fall back to rendering the embedded `events` snapshot verbatim (with a
   * defensive tombstone filter + the mount-once 404 staleness check). The PRESENCE
   * of this key — not its length — is the legacy-vs-live discriminator, so an
   * empty array (a live card over an empty window) still takes the live path.
   */
  objectIds?: string[];
}

export interface EmailCardData {
  messageId: string;
  threadId: string;
  fromName: string;
  fromEmail: string;
  subject: string;
  snippet: string;
  /** ISO 8601 timestamp. */
  date: string;
  isUnread: boolean;
  /** 1-based index of this match within the result set. */
  matchedIndex?: number;
  matchedOf?: number;
  senderPhotoUrl?: string | null;
}

/** Editable email draft the agent proposes; the user can tweak + send inline. */
export interface EmailDraftCardData {
  to: string;
  toName?: string;
  subject: string;
  /** May be empty (compose-pending) or hold the user's rough note to compose from. */
  body: string;
  /** Carbon-copy recipients (comma-separated email addresses). */
  cc?: string;
  /** Blind-carbon-copy recipients (comma-separated email addresses). */
  bcc?: string;
  /** Workspace contact this draft is addressed to; required to compose voices. */
  contactId?: string;
  /** Outreach type sent to the draft endpoint when composing. Defaults to 'casual'. */
  actionType?: string;
  /** True when the card is awaiting a voice choice (To filled, body not yet generated). */
  composePending?: boolean;
  /** Reply context (set when replying to an inbox email). When `threadId` is
   *  present the card is in REPLY mode: "Xavior write" drafts a contextual reply
   *  via /draft-reply (no contactId needed) and Send delivers in-thread. */
  threadId?: string;
  messageId?: string;
  replyMode?: boolean;
  /** #4747: when set, the card auto-fires the existing draft-reply path ONCE on
   *  mount so a reply card arrives PRE-DRAFTED (editable) instead of blank. The
   *  agent's email_compose tool sets this for reply cards with no seeded body. */
  autoDraftReply?: boolean;
  /** Account that owns the thread — sent as accountId so the reply threads onto
   *  and sends from the correct mailbox (matters for multi-account inboxes). */
  accountId?: string;
  /** Task B3: objectId of a Xavior doc/note attached to this draft as a PDF. When
   *  set, the card renders an attachment chip and its Send posts
   *  `docAttachments: [{ objectId: attachDocId }]` to the email /send route so the
   *  doc is rendered server-side to `<title>.pdf` and attached. Independent of the
   *  body — it survives the Rewrite/voice compose. */
  attachDocId?: string;
  /** Task B3: the attached doc's title, for the chip label ("📎 <title>.pdf"). */
  attachDocTitle?: string;
}

/** ONE card holding the priority inbox — emails the user needs to deal with,
 *  rendered as a clickable grid of chips. Clicking a chip opens a reply
 *  composer with a "Xavior write" option. */
export interface InboxCardData {
  emails: Array<{
    messageId: string;
    threadId: string;
    /** Account this mail belongs to (for replying from the right mailbox). */
    integrationId?: string;
    fromName: string;
    fromEmail: string;
    subject: string;
    snippet: string;
    /** ISO 8601 timestamp. */
    date: string;
    isUnread: boolean;
    senderPhotoUrl?: string | null;
    /** Cached AI reply draft (seeds "Xavior write" instantly if present). */
    aiDraft?: string | null;
  }>;
}

/** A newly created note. `noteId` is the workspace OBJECT id (click-through). */
export interface NoteCardData {
  noteId: string;
  title: string;
  bodyPreview: string;
}

/** A newly created or referenced Drive doc. `docId` is the workspace OBJECT id (click-through). */
export interface DocCardData {
  docId: string;
  title: string;
  bodyPreview: string;
}

/** ONE card holding ALL matched contacts (rendered as a grid). A matched cell's
 *  `id` is the workspace OBJECT id so opening resolves the ContactCardPopover.
 *  An UNRESOLVED invitee (e.g. an event attendee with no saved contact) has NO
 *  `id` — the web renders it as a non-clickable identity with an "Add contact"
 *  affordance instead of opening a contact. */
export interface ContactCardData {
  /** Optional header label for a FILTERED group (a role/company/title search,
   *  e.g. "soccer players") — shown on top of the grid instead of the generic
   *  "CONTACTS". Absent for a plain "all my contacts" listing. */
  label?: string;
  contacts: Array<{
    /** Absent for an unresolved invitee (no saved contact) → "Add contact". */
    id?: string;
    name: string;
    email?: string;
    phone?: string;
    title?: string;
    company?: string;
    photoUrl?: string;
    /** One-line AI profile ("who this person is") — shown on the single-contact card. */
    bio?: string;
  }>;
}

/** ONE card holding contacts SEGMENTED by a shared field — each group is a
 *  header ("ACME · 3") with its members listed beneath, so the user sees the
 *  grouping, not a flat list. Emitted by the agent's `group_contacts` tool.
 *  Each member `id` is the workspace OBJECT id (opens the ContactCardPopover). */
export interface ContactGroupCardData {
  /** The field the contacts were segmented by. */
  groupBy: 'company' | 'title' | 'relationship' | 'location';
  groups: Array<{
    /** The shared value (e.g. "Acme"), or a "No <field>" bucket label. */
    label: string;
    /** Member count — may equal contacts.length (no truncation today). */
    count: number;
    contacts: Array<{
      id: string;
      name: string;
      email?: string;
      phone?: string;
      title?: string;
      company?: string;
      photoUrl?: string;
    }>;
  }>;
}

/** One card per place. `placeId` is the workspace OBJECT id. */
export interface PlaceCardData {
  placeId: string;
  name: string;
  address?: string;
  category?: string;
  rating?: number;
}

/** ONE card holding ALL matched tasks (rendered as a checkbox list). Each `id`
 *  is the workspace OBJECT id so the inline checkbox writeback can PATCH it. */
export interface TaskCardData {
  tasks: Array<{
    id: string;
    title: string;
    completed: boolean;
    due?: string;
    boardId?: string;
    boardName?: string;
  }>;
}

/** End-of-turn "Open report" CTA for a report the agent built inline (#3793).
 *  Derived CLIENT-SIDE at stream finalize from the turn's report.init/
 *  report.section directives (helpers/report-result-card.ts) — the agent never
 *  emits this card itself. `moduleId` is the generated report module's id. */
export interface ReportCardData {
  moduleId: string;
  title: string;
}

/** Confirmation card for a chat-proposed MASS delete (3+ objects). The agent
 *  emits this INSTEAD of deleting (it never deletes 3+ itself); the user clicks
 *  Delete here and THIS card performs the deletes via the objects API — so the
 *  model can never bypass the prompt. `count` may exceed `previewItems.length`
 *  (the preview is capped at 5); `objectIds` always holds every id to delete.
 *  Each preview item carries its `boardId` so clicking it opens the SPECIFIC
 *  kanban board and highlights the task. */
export interface BulkDeleteConfirmCardData {
  /** Per-emit identity (server-generated, persisted in the message). The UI keys
   *  this card's resolved (deleted/cancelled) state on it so a hard refresh
   *  restores that state, while a later re-ask (new cardId) gets a fresh prompt. */
  cardId?: string;
  objectType: string;
  count: number;
  objectIds: string[];
  previewItems: Array<{ id: string; title: string; boardId?: string }>;
  /** Workspace + conversation the card belongs to. Present so a CONFIRMED delete
   *  (which happens client-side) can be recorded back to the conversation — the
   *  agent otherwise never learns the user clicked Delete and later claims nothing
   *  was deleted. When absent, the write-back is skipped (card still deletes). */
  workspaceId?: string;
  conversationId?: string;
}

/** Confirmation card for a chat-proposed board ARCHIVE ("delete my sales board").
 *  Boards are archived (reversible), never hard-deleted — the agent emits this
 *  instead of touching anything, and THIS card performs the archive on the user's
 *  click by writing the module config (stamping `archivedAt` on the board), so the
 *  model can never bypass the prompt. Its tasks are kept with the board. */
export interface BoardArchiveConfirmCardData {
  /** Per-emit identity (server-generated); the UI keys the resolved state on it. */
  cardId?: string;
  /** Workspace + module that own the board, needed for the config write. */
  workspaceId: string;
  moduleId: string;
  boardId: string;
  boardName: string;
  /** Total tasks on the board (may exceed previewTasks.length). */
  taskCount: number;
  /** Up to a handful of the board's tasks, for the card body. */
  previewTasks: Array<{ id: string; title: string }>;
}

export interface ResultCard {
  kind: ResultCardKind;
  /** For kind === 'email', `data` is an {@link EmailCardData}. */
  data: Record<string, unknown>;
}

/**
 * Extract the first balanced JSON array `[...]` from `source`, respecting string
 * literals and escapes so a bracket inside a string doesn't end the scan. Skips
 * leading whitespace. Returns the `[...]` substring, or `null` when the array is
 * absent or never closes (truncated). Total — never throws.
 *
 * This bounds the parse to exactly the cards array so that any trailing content
 * after it (assistant prose, a SECOND marker block from a double-append, a
 * stray newline) cannot poison `JSON.parse`.
 */
function sliceFirstJsonArray(source: string): string | null {
  let i = 0;
  while (i < source.length && /\s/.test(source[i] ?? '')) i += 1;
  if (source[i] !== '[') return null;

  const start = i;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (; i < source.length; i += 1) {
    const char = source[i] ?? '';
    if (inString) {
      if (escaped) { escaped = false; continue; }
      if (char === '\\') { escaped = true; continue; }
      if (char === '"') inString = false;
      continue;
    }
    if (char === '"') { inString = true; continue; }
    if (char === '[' || char === '{') { depth += 1; continue; }
    if (char === ']' || char === '}') {
      depth -= 1;
      if (depth === 0) return source.slice(start, i + 1);
    }
  }
  return null;
}

/**
 * Escape raw control characters (U+0000–U+001F) that appear INSIDE string
 * literals so a payload carrying an unescaped newline/tab (which `JSON.parse`
 * rejects as a "bad control character") can still be recovered. Structural
 * whitespace between tokens is left untouched. Total — never throws.
 *
 * `JSON.stringify` never emits raw control chars, so this only matters for
 * payloads that were assembled or mutated outside the normal producer path; it
 * is a recovery fallback, attempted only after a strict parse fails.
 */
function escapeControlCharsInStrings(source: string): string {
  let out = '';
  let inString = false;
  let escaped = false;
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i] ?? '';
    const code = source.charCodeAt(i);
    if (inString) {
      if (escaped) { out += char; escaped = false; continue; }
      if (char === '\\') { out += char; escaped = true; continue; }
      if (char === '"') { out += char; inString = false; continue; }
      if (code < 0x20) {
        out += char === '\n' ? '\\n'
          : char === '\r' ? '\\r'
          : char === '\t' ? '\\t'
          : '\\u' + code.toString(16).padStart(4, '0');
        continue;
      }
      out += char;
      continue;
    }
    if (char === '"') inString = true;
    out += char;
  }
  return out;
}

/** A parsed array element is renderable only when it is an object carrying a
 *  string `kind` and an object `data`. Used to drop a single malformed card
 *  without discarding its valid siblings (partial recovery). Unknown `kind`s
 *  pass here — {@link ResultCardKind} routing handles them downstream. */
function isRenderableCard(value: unknown): value is ResultCard {
  if (!value || typeof value !== 'object') return false;
  const card = value as { kind?: unknown; data?: unknown };
  return typeof card.kind === 'string'
    && typeof card.data === 'object'
    && card.data !== null;
}

/**
 * Return the length of the longest suffix of `content` that equals a non-empty
 * prefix of `RESULT_CARDS_MARKER`. Zero when no such suffix exists.
 *
 * Used during streaming to detect a partial marker that has arrived across SSE
 * chunks but is not yet complete, so the in-flight fragment is never shown as
 * visible text.
 */
function trailingPartialMarkerLength(content: string): number {
  const marker = RESULT_CARDS_MARKER;
  // Walk candidate prefix lengths from longest possible down to 1.
  const maxLen = Math.min(content.length, marker.length - 1);
  for (let len = maxLen; len >= 1; len -= 1) {
    if (content.endsWith(marker.slice(0, len))) return len;
  }
  return 0;
}

/**
 * Split a persisted assistant message into its plain-markdown body and any
 * trailing result cards.
 *
 * Locates {@link RESULT_CARDS_MARKER}: everything before it (trimEnd) is
 * `cleanContent`; the substring after the marker is parsed as `ResultCard[]`.
 *
 * FAIL-CLOSED contract: on ANY failure (bad JSON, non-array, truncated payload)
 * the marker block is DROPPED — we return the pre-marker prose with `cards: []`,
 * never the original marker-bearing string. The internal protocol marker and
 * raw JSON must never reach the rendered chat as visible text, even when the
 * payload is malformed. Worst case the cards are lost; the chat stays clean.
 *
 * STREAMING: when the full marker has not yet arrived, a trailing partial-marker
 * prefix (e.g. "@@XAVIOR_RESU" at end of content) is trimmed from cleanContent
 * so the in-flight fragment is never shown as visible text.
 *
 * Robustness: the parse is bounded to the first balanced `[...]` array (so
 * trailing prose or a second marker block can't poison it), retries once with
 * control characters escaped (recovers a stray unescaped newline), and filters
 * to renderable cards so one malformed card doesn't drop its valid siblings.
 */
/**
 * Identity key for a result card, used to dedupe. Several agent tools each emit
 * their own doc/note/place card unconditionally (search, show_note, get_object,
 * create_object), so a turn that touches ONE object through two of them serializes
 * the same card twice → duplicate cards in the bubble. Key single-entity cards on
 * `kind + <id>` (so a create's preview and a follow-up get's preview of the SAME
 * doc collapse even when the excerpt differs); aggregate/other cards fall back to
 * `kind + full data` so only byte-identical ones collapse.
 *
 * MUST stay in lockstep with resultCardIdentity in
 * apps/x-agent/src/features/chat/chat-helpers.ts (the agent-side persist path
 * dedupes with the same key; web cannot be imported there).
 */
function resultCardIdentity(card: ResultCard): string {
  const d = card.data ?? {};
  // email_draft (compose/reply) cards are emitted ONCE per compose — never by
  // several emitters touching one stored object — so an id-based collapse buys
  // nothing and actively harms: two distinct drafts to the SAME contact in one
  // turn (e.g. attaching two different docs) both carry that contactId and would
  // wrongly collapse to a single card, silently dropping the second. Key them on
  // full data so only byte-identical duplicates dedupe. MUST stay in lockstep
  // with resultCardIdentity in apps/x-agent/.../chat-helpers.ts.
  if (card.kind === 'email_draft') return `${card.kind}:${JSON.stringify(d)}`;
  const singleId =
    d.docId ?? d.noteId ?? d.placeId ?? d.messageId ?? d.eventId ?? d.contactId ?? d.taskId ?? d.id;
  return singleId != null ? `${card.kind}:${String(singleId)}` : `${card.kind}:${JSON.stringify(d)}`;
}

/** Drop duplicate cards (same target identity), keeping the first occurrence. */
export function dedupeResultCards(cards: ResultCard[]): ResultCard[] {
  const seen = new Set<string>();
  const out: ResultCard[] = [];
  for (const card of cards) {
    const key = resultCardIdentity(card);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(card);
  }
  return out;
}

export function splitResultCards(content: string): { cleanContent: string; cards: ResultCard[] } {
  const markerIndex = content.indexOf(RESULT_CARDS_MARKER);
  if (markerIndex === -1) {
    // No full marker found. Check for a trailing partial-marker prefix and trim it.
    const partialLen = trailingPartialMarkerLength(content);
    if (partialLen > 0) {
      return { cleanContent: content.slice(0, content.length - partialLen).trimEnd(), cards: [] };
    }
    return { cleanContent: content, cards: [] };
  }

  const cleanContent = content.slice(0, markerIndex).trimEnd();
  const tail = content.slice(markerIndex + RESULT_CARDS_MARKER.length);
  const arrayText = sliceFirstJsonArray(tail) ?? tail;

  const tryParse = (text: string): ResultCard[] | null => {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!Array.isArray(parsed)) return null;
      return parsed.filter(isRenderableCard);
    } catch {
      return null;
    }
  };

  const cards = tryParse(arrayText) ?? tryParse(escapeControlCharsInStrings(arrayText));
  // Fail closed: drop the marker block regardless of parse outcome. Dedupe so a
  // doc/note/place touched by two card-emitting tools in one turn (or a
  // double-appended marker) renders at most one card per object.
  return { cleanContent, cards: dedupeResultCards(cards ?? []) };
}
